'use client';

import { useServerTranslations } from "@/_ui/lib/i18n/get-dictionary";
import { useGoogleReCaptcha } from "@/lib";
import { requestLoginToken, verifyLoginToken } from "@/actions/auth";
import {
    clearStoredEmailAttribution,
    getStoredEmailAttribution,
    persistEmailAttribution,
    toAnalyticsEventParams,
} from "@/lib/analytics/email-attribution";
import { event } from "@/lib/analytics/gtag";
import { RETURN_PATH_PARAM, resolveReturnPath, withReturnPath } from "@/lib/auth/return-path";
import { Alert, Card, Header } from '@ui/composites';
import { Button, Input, LocalizedLink } from '@ui/primitives';
import { Section } from '@ui/layouts';
import { SocialConnect } from "@/components/forms/socialConnect";
import classNames from "classnames";
import { useRouter, useSearchParams } from "next/navigation";
import { useActionState, useEffect, useState, useTransition } from "react";
import { useForm } from "react-hook-form";

interface LoginClientProps {
    locale: string;
    dictionary: Record<string, any>;
    recaptchaSiteKey: string;
}

const translateMessage = (t: Function, message?: string) => {
    if (!message) {
        return "";
    }

    const translated = t(message);
    return typeof translated === "string" && translated !== message ? translated : message;
};

const RequestTokenForm = ({ dictionary, locale, recaptchaSiteKey, initialEmail, onFormSuccess }: { dictionary: Record<string, any>, locale: string, recaptchaSiteKey: string, initialEmail?: string, onFormSuccess: (email: string) => void }) => {
    const { t, ts } = useServerTranslations(dictionary);
    const router = useRouter();

    const [state, formAction] = useActionState(requestLoginToken, undefined);
    const [isPending, startTransition] = useTransition();
    const { register, formState: { errors }, getValues, setValue } = useForm<{ email: string }>();
    const { getToken } = useGoogleReCaptcha(recaptchaSiteKey, locale);

    useEffect(() => {
        if (initialEmail) {
            setValue('email', initialEmail);
        }
    }, [initialEmail, setValue]);

    useEffect(() => {
        if (state?.isError === false) {
            onFormSuccess(getValues("email"));
        }
        if (state?.isError && state?.message === 'errors.account_not_confirmed') {
            const email = getValues("email");
            if (email) sessionStorage.setItem('registration_email', email);
            router.push(`/${locale}/registration-pending`);
        }
    }, [state, onFormSuccess, getValues, router, locale]);

    return (
        <form
            action={async (formData) => {
                formData.set('recaptchaToken', await getToken('login'));
                startTransition(() => formAction(formData));
            }}
            className="space-y-4 sm:space-y-6"
            noValidate
            aria-describedby={state?.isError ? "form-error" : undefined}
        >
            <div role="status" aria-live="polite" aria-atomic="true">
                {isPending && (
                    <Alert className="mb-4">
                        {t('auth.sending_confirmation')}
                    </Alert>
                )}
                {state?.isError === false && (
                    <Alert className="mb-4">
                        {translateMessage(t, state.message)}
                    </Alert>
                )}
                {state?.isError && (
                    <Alert className="mb-4">
                        {translateMessage(t, state.message)}
                        {state.message === 'errors.account_not_found' && (
                            <LocalizedLink locale={locale} variant="primary" href="/register" className="block mt-2">
                                {t('auth.links_register')}
                            </LocalizedLink>
                        )}
                    </Alert>
                )}
            </div>

            <div className="space-y-2">
                <label
                    htmlFor="email"
                    className={classNames(
                        "block text-sm font-medium transition-colors",
                        { 'text-error-700': errors.email },
                        { 'text-gray-700': !errors.email }
                    )}
                >
                    {t('forms.email_label')}
                    <span className="text-error-500 ml-1" aria-label="required">*</span>
                </label>
                <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    disabled={isPending}
                    placeholder={ts('forms.email_placeholder')}
                    {...register('email', {
                        setValueAs: (value) => typeof value === 'string' ? value.trim().toLowerCase() : value,
                        required: ts('forms.email_errors_required'),
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: ts('forms.email_errors_invalid')
                        }
                    })}
                    name="email"
                    error={errors.email?.message}
                    className="w-full text-base sm:text-sm"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : 'email-help'}
                />
                {!errors.email && (
                    <p id="email-help" className="text-xs text-gray-500">
                        {t('auth.login_email_help')}
                    </p>
                )}
            </div>

            <Button type="submit"
                variant="secondary"
                disabled={isPending}
                loading={isPending}
                icon={<i className="fas fa-envelope mr-2" />}
                className="w-full">
                {t('auth.links_send_code')}
            </Button>
        </form>
    );
};

const VerifyTokenForm = ({ dictionary, email, destination, onBack }: { dictionary: Record<string, any>, email: string, destination: string, onBack: () => void }) => {
    const { t, ts } = useServerTranslations(dictionary);
    const [state, formAction] = useActionState(verifyLoginToken, undefined);
    const [isPending, startTransition] = useTransition();
    const { register, formState: { errors } } = useForm<{ token: string }>();
    const router = useRouter();

    useEffect(() => {
        if (state?.isError === false) {
            event({ action: 'login', category: 'auth', label: 'passwordless' });
            const attribution = getStoredEmailAttribution();
            if (attribution) {
                event({
                    action: 'auth_email_login_success',
                    category: 'auth',
                    label: attribution.utm_campaign || 'email',
                    ...toAnalyticsEventParams(attribution),
                });
                clearStoredEmailAttribution();
            }
            router.push(destination);
        }
    }, [state, router, destination]);

    return (
        <form
            action={(formData) => startTransition(() => formAction(formData))}
            className="space-y-4 sm:space-y-6"
            noValidate
            aria-describedby={state?.isError ? "token-form-error" : undefined}
        >
            <input type="hidden" name="email" value={email} />
            <div role="status" aria-live="polite" aria-atomic="true">
                {isPending && <Alert className="mb-4">{t('auth.login_verifying')}</Alert>}
                {state?.isError === false && <Alert className="mb-4">{translateMessage(t, state.message)}</Alert>}
                {state?.isError && <Alert className="mb-4">{translateMessage(t, state.message)}</Alert>}
            </div>

            <div className="space-y-2">
                <label htmlFor="token" className={classNames("block text-sm font-medium", { 'text-[var(--color-error-700)]': errors.token })}>
                    {t('auth.login_token_label')}
                    <span className="text-[var(--color-error-500)] ml-1">*</span>
                </label>
                <Input
                    id="token"
                    type="text"
                    autoComplete="one-time-code"
                    disabled={isPending}
                    placeholder={ts('forms.token_placeholder')}
                    {...register('token', {
                        setValueAs: (value) => typeof value === 'string' ? value.replace(/\D/g, '').slice(0, 6) : value,
                        required: ts('forms.token_errors_required'),
                        pattern: { value: /^\d{6}$/, message: ts('forms.token_errors_invalid') }
                    })}
                    name="token"
                    error={errors.token?.message}
                    className="w-full text-base sm:text-sm text-center tracking-widest"
                    inputMode="numeric"
                    maxLength={6}
                />
            </div>

            <div className="flex gap-3">
                <Button type="submit"
                    variant="secondary"
                    disabled={isPending}
                    loading={isPending}
                    className="flex-1">
                    {t("auth.links_login")}
                </Button>
            </div>
            <Button
                type="button"
                variant="link"
                onClick={onBack}
                className="w-full">
                {t("actions.back")}
            </Button>
        </form>
    );
};

export const LoginClient = ({ locale, dictionary, recaptchaSiteKey }: LoginClientProps) => {
    const { t } = useServerTranslations(dictionary);
    const searchParams = useSearchParams();
    const [step, setStep] = useState<0 | 1>(0);
    const [email, setEmail] = useState("");
    const [initialEmail, setInitialEmail] = useState("");
    const [hasTrackedEmailVisit, setHasTrackedEmailVisit] = useState(false);

    // Where to send the user once logged in: back to whatever they were trying to do.
    const returnPath = searchParams.get(RETURN_PATH_PARAM);
    const destination = resolveReturnPath(returnPath, locale);

    useEffect(() => {
        const savedEmail = sessionStorage.getItem('registration_email');
        if (savedEmail) {
            setInitialEmail(savedEmail);
        }
    }, []);

    useEffect(() => {
        if (hasTrackedEmailVisit) {
            return;
        }

        const attribution = persistEmailAttribution(searchParams);
        if (!attribution) {
            return;
        }

        event({
            action: 'auth_email_visit',
            category: 'auth',
            label: 'login',
            ...toAnalyticsEventParams(attribution),
        });
        setHasTrackedEmailVisit(true);
    }, [searchParams, hasTrackedEmailVisit]);

    const handleSuccess = (email: string) => {
        setEmail(email)
        setStep(1)
    }

    return (
        <Section container className="py-8">
            <div className="max-w-md mx-auto w-full">
                <div className="space-y-6 sm:space-y-8">
                    <Header
                        title={t('auth.login_title')}
                        description={step === 0 ? t('auth.login_subtitle') : t('auth.login_verify_subtitle')}
                        align="center"
                        size="xl"
                    />

                    <Card className="p-6">
                        {step === 0 ? (
                            <>
                                <RequestTokenForm dictionary={dictionary} locale={locale} recaptchaSiteKey={recaptchaSiteKey} initialEmail={initialEmail} onFormSuccess={handleSuccess} />
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <p className="text-sm text-gray-500 text-center mb-4">{t('auth.use_services')}</p>
                                    <SocialConnect locale={locale} destination={destination} />
                                </div>
                            </>
                        ) : (
                            <VerifyTokenForm dictionary={dictionary} email={email} destination={destination} onBack={() => setStep(0)} />
                        )}
                    </Card>

                    {step === 0 && (
                        <nav className="text-center">
                            <p className="text-sm text-gray-500 mb-4">
                                {t('auth.register_dont_have_account')}
                            </p>
                            <nav className="text-center">
                                <LocalizedLink
                                    locale={locale}
                                    variant="primary"
                                    href={withReturnPath('/register', returnPath)}>
                                    <i className="fas fa-user-plus mr-2" />
                                    {t('auth.links_register')}
                                </LocalizedLink>
                            </nav>
                        </nav>
                    )}
                </div>
            </div>
        </Section>
    );
};
